// Ruting aplikacji – strony ładowane lazy (code splitting), mniejszy początkowy JS
import React, { Suspense, lazy } from 'react';
import { Route, Routes as RouterRoutes, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isWiseUiEnabled } from 'config/wiseUi';
import useRoutes from './useRoutes';
import { useNotificationToSnackbar } from 'hooks';
import {
  DashboardSubLayout,
  DefaultLayout,
  MainLayout,
  TraderPanelLayout,
  TraderPanelProtectedRoute,
  DevStudioLayout
} from 'components/layouts';
import { ManagementPanelLayout } from 'plugins/components/layouts';
import {
  // ProfileLoading,
  Agreements,
  ChangePasswordForm,
  NewVersionPopup
} from 'components/containers';
import { Modal } from 'components/controls';

// Lazy-loaded pages – ładowane dopiero przy wejściu na daną ścieżkę (mniejszy TBT, szybszy FCP)
const Home = lazy(() => import('pages/Home'));
const Login = lazy(() => import('pages/Login'));
const ForgotPassword = lazy(() => import('pages/ForgotPassword'));
const ResetPassword = lazy(() => import('pages/ResetPassword'));
const DownloadExport = lazy(() => import('pages/DownloadExport'));
const Cart = lazy(() => import('pages/Cart'));
const Checkout = lazy(() => import('pages/Checkout'));
const Dashboard = lazy(() => import('pages/Dashboard'));
const Carts = lazy(() => import('pages/Carts'));
const CartsImport = lazy(() => import('pages/CartsImport'));
const Clients = lazy(() => import('pages/Clients'));
const Deliveries = lazy(() => import('pages/Deliveries'));
const Documents = lazy(() => import('pages/Documents'));
const Export = lazy(() => import('pages/Export'));
const Faq = lazy(() => import('pages/Faq'));
const Consent = lazy(() => import('pages/Consent'));
// import MessageSettings from 'pages/MessageSettings'; // commented out in original

const MyAccount = lazy(() => import('pages/MyAccount'));
const OffersDedicated = lazy(() => import('pages/OffersDedicated'));
const OffersPeriodic = lazy(() => import('pages/OffersPeriodic'));
const Orders = lazy(() => import('pages/Orders'));
const Order = lazy(() => import('pages/Order'));
const Payments = lazy(() => import('pages/Payments'));
const Receivers = lazy(() => import('pages/Receivers'));
const ShoppingLists = lazy(() => import('pages/ShoppingLists'));
const Terms = lazy(() => import('pages/Terms'));
const Users = lazy(() => import('pages/Users'));
const CmsSections = lazy(() => import('pages/CmsSections'));
const CmsSection = lazy(() => import('pages/CmsSection'));
const CmsArticles = lazy(() => import('pages/CmsArticles'));
const CmsArticle = lazy(() => import('pages/CmsArticle'));
const CmsMedia = lazy(() => import('pages/CmsMedia'));
const UrlResolver = lazy(() => import('pages/UrlResolver'));
const WorkInProgress = lazy(() => import('pages/WorkInProgress'));
const Products = lazy(() => import('pages/Products'));
const Product = lazy(() => import('pages/Product'));
const WiseProduct = lazy(() => import('pages/wise/WiseProduct'));
const Register = lazy(() => import('pages/Register'));
const ProductPage = isWiseUiEnabled() ? WiseProduct : Product;
const RegisterEmailConfirm = lazy(() => import('pages/RegisterEmailConfirm'));
const ThankYouRegister = lazy(() => import('pages/ThankYouRegister'));
const PaymentSuccess = lazy(() => import('pages/PaymentSuccess'));
const PaymentStatus = lazy(() => import('pages/PaymentStatus'));
const PanelDashboard = lazy(() => import('pages/PanelDashboard'));
const PanelDynamicPage = lazy(() => import('pages/PanelDynamicPage'));
const TraderDashboard = lazy(() => import('pages/TraderDashboard'));
const TraderPanelClientsList = lazy(() => import('pages/TraderPanelClientsList'));
const TraderPanelClientCard = lazy(() => import('pages/TraderPanelClientCard'));
const TraderPanelClientForm = lazy(() => import('pages/TraderPanelClientForm'));
const TraderPanelClientOffers = lazy(() => import('pages/TraderPanelClientOffers'));
const TraderPanelClientCarts = lazy(() => import('pages/TraderPanelClientCarts'));
const TraderPanelClientsCartList = lazy (() => import('pages/TraderPanelClientsCartList'));
const TraderPanelClientsOrdersList = lazy (() => import('pages/TraderPanelClientsOrdersList'));
const TraderPanelClientOrders = lazy(() => import('pages/TraderPanelClientOrders'));
const TraderPanelClientDocuments = lazy(() => import('pages/TraderPanelClientDocuments'));
const TraderPanelClientPayments = lazy (() => import('pages/TraderPanelClientPayments'));
const TraderPanelClientShoppingLists = lazy (() => import('pages/TraderPanelClientShoppingLists'));
const TraderPanelProductCard = lazy(() => import('pages/TraderPanelProductCard'));
const TraderPanelProductsList = lazy (()=> import('pages/TraderPanelProductsList'));
const TraderPanelTasksList = lazy(() => import('pages/TraderPanelTasksList'));
const TraderPanelTasksForm = lazy(() => import('pages/TraderPanelTasksForm'));
const TraderPanelOffers = lazy (() => import('pages/TraderPanelOffers'));
const Confirmation = lazy(() => import('pages/Checkout/components/Confirmation'));
const ReturnsComplaints = lazy(() => import('pages/ReturnsComplaints'));
const ReturnComplaint = lazy(() => import('pages/ReturnComplaint'));
const KnowledgeBaseHome = lazy(() => import('pages/KnowledgeBaseHome'));
const KnowledgeBaseArticle = lazy(() => import('pages/KnowledgeBaseArticle'));
const KnowledgeBaseAll = lazy(() => import('pages/KnowledgeBaseAll'));
const DevStudio = lazy(() => import('pages/DevStudio'));
const ProcessEditor = lazy(() => import('pages/DevStudio/ProcessEditor'));
const EntityEditor = lazy(() => import('pages/DevStudio/EntityEditor'));

/** Fallback podczas ładowania lazy route – minimalny, bez dodatkowego JS */
const RouteFallback = null;

const Routes = () => {
  const { t } = useTranslation();
  useNotificationToSnackbar();
  const {
    // isProfileLoading,
    isAuthenticated,
    redirectUrl,
    urlPrefix,
    // isOpenProfileReady,
    profile,
    notificationModal,
    dismissNotificationModal
  } = useRoutes();

  // wymuszenie zmiany hasła
  if (profile?.change_password && profile?.role !== 'ROLE_OPEN_PROFILE') {
    return (
      <DefaultLayout>
        <Modal title={t('Ustawienie hasła')}>
          <ChangePasswordForm />
        </Modal>
      </DefaultLayout>
    );
  }

  if (profile?.consents_required && profile?.role !== 'ROLE_OPEN_PROFILE') {
    return (
      <DefaultLayout>
        <Modal title="" onClose={() => null}>
          <Agreements userId={profile.id} />
        </Modal>
      </DefaultLayout>
    );
  }

  return (
    <>
      <Suspense fallback={RouteFallback}>
        <RouterRoutes>
          <Route element={<DevStudioLayout />}>
            <Route path={`${urlPrefix}/dev-studio`} element={<DevStudio />} />
            <Route path={`${urlPrefix}/dev-studio/editor/:processId`} element={<ProcessEditor />} />
            <Route path={`${urlPrefix}/dev-studio/entity/:entityUuid`} element={<EntityEditor />} />
          </Route>
          <Route element={isAuthenticated ? <MainLayout /> : <DefaultLayout />}>
            <Route path={`${urlPrefix}/login`} element={<Login />} />
            <Route path={`${urlPrefix}/forgot-password`} element={<ForgotPassword />} />
            <Route path={`${urlPrefix}/reset-password/:token`} element={<ResetPassword />} />
          </Route>
          <Route element={<MainLayout />}>
            <Route path={`${urlPrefix}`} element={<Home />} />
            <Route path={`${urlPrefix}/download/export/:hash`} element={<DownloadExport />} />
            {profile?.role !== 'ROLE_OPEN_PROFILE' ? (
              <>
                <Route path={`${urlPrefix}/cart/:id`} element={<Cart />} />
                <Route path={`${urlPrefix}/checkout/:cartId`} element={<Checkout />} />
                <Route
                  path={`${urlPrefix}/checkout/success/:cartId`}
                  element={<Confirmation cartId={37} />}
                />
                <Route
                  path={`${urlPrefix}/dashboard`}
                  element={<DashboardSubLayout urlPrefix={urlPrefix} />}>
                  <Route index element={<Dashboard />} />
                  <Route path="carts" element={<Carts />} />
                  <Route path="carts/import" element={<CartsImport />} />
                  <Route path="clients" element={<Clients />} />
                  <Route path="deliveries" element={<Deliveries />} />
                  <Route path="documents" element={<Documents />} />
                  <Route path="returns-complaints" element={<ReturnsComplaints />} />
                  <Route path="returns-complaints/:itemId" element={<ReturnComplaint />} />
                  <Route path="export" element={<Export />} />
                  <Route path="faq" element={<Faq />} />
                  <Route path="consent" element={<Consent />} />
                  {/* https://jira.sente.pl/browse/AGW-1141 */}
                  {/* <Route path="message-settings" element={<MessageSettings />} /> */}
                  <Route path="my-account" element={<MyAccount />} />
                  <Route path="offers/dedicated" element={<OffersDedicated />} />
                  <Route path="offers/periodic" element={<OffersPeriodic />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="orders/:orderId" element={<Order />} />
                  <Route path="payments" element={<Payments />} />
                  <Route path="receivers" element={<Receivers />} />
                  <Route path="shopping-lists" element={<ShoppingLists />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="users" element={<Users />} />
                  <Route path="cms/sections" element={<CmsSections />} />
                  <Route path="cms/sections/:id" element={<CmsSection />} />
                  <Route path="cms/articles" element={<CmsArticles />} />
                  <Route path="cms/articles/:id" element={<CmsArticle />} />
                  <Route path="cms/articles/create" element={<CmsArticle />} />
                  <Route path="cms/media" element={<CmsMedia />} />
                  <Route path="*" element={<UrlResolver />} />
                </Route>
              </>
            ) : (
              <>
                <Route path={`${urlPrefix}/cart/:id`} element={<Navigate to="/" replace />} />
                <Route
                  path={`${urlPrefix}/checkout/:cartId`}
                  element={<Navigate to="/" replace />}
                />
                <Route
                  path={`${urlPrefix}/checkout/success/:cartId`}
                  element={<Navigate to="/" replace />}
                />
                <Route path={`${urlPrefix}/dashboard/*`} element={<Navigate to="/" replace />} />
              </>
            )}
            <Route path={`${urlPrefix}/wip`} element={<WorkInProgress />} />
            <Route path={`${urlPrefix}/knowledge-base`} element={<KnowledgeBaseHome />} />
            <Route path={`${urlPrefix}/knowledge-base/all`} element={<KnowledgeBaseAll />} />
            <Route path={`${urlPrefix}/knowledge-base/:id`} element={<KnowledgeBaseArticle />} />
            <Route path={`${urlPrefix}/products`} element={<Products />} />
            <Route path={`${urlPrefix}/products/:id`} element={<ProductPage />} />
            <Route path={`${urlPrefix}/register`} element={<Register />} />
            <Route path="confirm_register" element={<RegisterEmailConfirm />} />
            <Route path={`${urlPrefix}/thankyou_register`} element={<ThankYouRegister />} />
            <Route path="payment/success" element={<PaymentSuccess />} />
            <Route path={`${urlPrefix}/payment/status`} element={<PaymentStatus />} />
            <Route path={`${urlPrefix}/*`} element={<UrlResolver />} />
          </Route>
          <Route element={<TraderPanelProtectedRoute />}>
            <Route element={<TraderPanelLayout />}>
              {/* Panel handlowca */}
              <Route path={`${urlPrefix}/trader`}>
                <Route index element={<TraderDashboard />} />
                <Route path="tasks" element={<TraderPanelTasksList />} />
                <Route path="tasks/new" element={<TraderPanelTasksForm />} />
                <Route path="tasks/:id" element={<TraderPanelTasksForm />} />
                <Route path="clients" element={<TraderPanelClientsList />} />
                <Route path="client-carts" element={<TraderPanelClientsCartList />} />
                <Route path="offers" element={<TraderPanelOffers />} />
                <Route path="sales" element={<TraderPanelClientsOrdersList />} />
                <Route path="clients/new" element={<TraderPanelClientForm />} />
                <Route path="clients/:id/edit" element={<TraderPanelClientForm />} />
                <Route path="clients/:id/offers" element={<TraderPanelClientOffers />} />
                <Route path="clients/:id/carts" element={<TraderPanelClientCarts />} />
                <Route path="clients/:id/orders" element={<TraderPanelClientOrders />} />
                <Route path="clients/:id/documents" element={<TraderPanelClientDocuments />} />
                <Route path="clients/:id/payments" element={<TraderPanelClientPayments />} />
                <Route path="clients/:id/shopping-lists" element={<TraderPanelClientShoppingLists />} />
                <Route path="clients/:id/products" element={<TraderPanelProductsList />} />
                <Route
                  path="clients/:id/products/:productId"
                  element={<TraderPanelProductCard />}
                />
                <Route path="products" element={<TraderPanelProductsList />} />
                <Route path="products/:productId" element={<TraderPanelProductCard />} />
                <Route path="clients/:id" element={<TraderPanelClientCard />} />
                <Route path={'*'} element={<TraderDashboard />} />
              </Route>
            </Route>
          </Route>
          <Route element={<ManagementPanelLayout />}>
            {/* Panel administratora/tradera */}
            <Route path={`${urlPrefix}/managment_panel`}>
              <Route index element={<PanelDashboard />} />
              <Route path="cms/sections" element={<CmsSections isAdminPanel />} />
              <Route path="cms/sections/:id" element={<CmsSection />} />
              <Route path="cms/articles" element={<CmsArticles isAdminPanel />} />
              <Route path="cms/articles/:id" element={<CmsArticle />} />
              <Route path="cms/articles/create" element={<CmsArticle />} />
              <Route path="cms/media" element={<CmsMedia isAdminPanel />} />
              <Route path="dynamic_page/:pageSymbol" element={<PanelDynamicPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to={redirectUrl} replace />} />
        </RouterRoutes>
      </Suspense>

      {notificationModal && (
        <Modal title={notificationModal.title} onClose={dismissNotificationModal}>
          <div dangerouslySetInnerHTML={{ __html: notificationModal.content }} />
        </Modal>
      )}

      <NewVersionPopup />
    </>
  );
};

export default Routes;
